import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Form, Button, InputGroup, Row, Col, Image, Modal, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import * as Icon from "react-bootstrap-icons"
import { spacer } from "@src/constants"
import { DeviceSearch, InputField, Page } from "@src/components"
import { DEVICE_TYPE } from "@src/devices/types"
import { THEFT } from "@src/api/types"
import {
  getDeviceSerialnumber,
  postPartnerDeviceSerialnumberThefts,
  getPartnerDeviceSerialnumberTheftsActive,
  patchTheftsId,
  deleteTheftsId,
} from "@src/api"
import { usePartner } from "@src/partner"

type THEFT_TYPE = {
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  specialFeature: string
  description: string
  police_case_number: string
  checkbox: boolean
}
const DEFAULT_THEFT = {
  startDate: null,
  startTime: null,
  endDate: null,
  endTime: null,
  specialFeature: null,
  description: null,
  police_case_number: "",
  checkbox: false,
}
const convertToTimestamp = (date: string, time: string): number =>
  new Date(`${date} ${time}`).getTime()

/** Format as API expects: 'YYYY-MM-DD HH:mm:ss +0100' */
const toApiTimestamp = (date: string, time: string): string => {
  const timeWithSeconds = time.length === 5 ? `${time}:00` : time
  const d = new Date(`${date}T${timeWithSeconds}`)
  const offsetMin = -d.getTimezoneOffset()
  const sign = offsetMin >= 0 ? "+" : "-"
  const hours = String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, "0")
  const mins = String(Math.abs(offsetMin) % 60).padStart(2, "0")
  return `${date} ${timeWithSeconds} ${sign}${hours}${mins}`
}

const styles = {
  displayFlex: { display: "flex" },
  headerView: { display: "flex", marginBottom: spacer[2] },
  buttonView: { marginLeft: spacer[2] },
}

export const TheftReportScreen: React.FC = () => {
  const { serialnumber: serialnumberParam = "" } = useParams()
  const { t } = useTranslation()
  const { partner } = usePartner()
  const [serialnumber, setSerialnumber] = useState<string>(serialnumberParam)
  const [device, setDevice] = useState<DEVICE_TYPE>()
  const [serialnumberInput, setSerialnumberInput] = useState<string>(serialnumberParam)
  const [theft, setTheft] = useState<THEFT_TYPE>(DEFAULT_THEFT)
  const [modal, setModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeTheft, setActiveTheft] = useState<THEFT | null>(null)
  const [activeTheftLoadFailed, setActiveTheftLoadFailed] = useState<boolean>(false)
  const [savingPoliceNumber, setSavingPoliceNumber] = useState<boolean>(false)

  const today = new Date().toISOString().split("T")[0]
  const isStartBeforeEnd =
    convertToTimestamp(theft.startDate, theft.startTime) <
    convertToTimestamp(theft.endDate, theft.endTime)
  const theftStartAndEndFilled =
    theft.startDate && theft.startTime && theft.endDate && theft.endTime
  const reportTheftAllowed = isStartBeforeEnd && theftStartAndEndFilled && theft.checkbox

  const fetchDevice = () =>
    partner?.uuid &&
    getDeviceSerialnumber(partner?.uuid, serialnumber).then(({ response, data }) => {
      if (response.ok) setDevice(data)
    })

  useEffect(() => {
    fetchDevice()
  }, [partner, serialnumber])

  useEffect(() => {
    if (device?.is_stolen && partner?.uuid && serialnumber) {
      setActiveTheftLoadFailed(false)
      getPartnerDeviceSerialnumberTheftsActive(partner.uuid, serialnumber).then(
        ({ response, data }) => {
          if (response.ok && data) {
            setActiveTheft(data)
            setActiveTheftLoadFailed(false)
          } else {
            setActiveTheft(null)
            setActiveTheftLoadFailed(true)
          }
        },
      )
    } else {
      setActiveTheft(null)
      setActiveTheftLoadFailed(false)
    }
  }, [device?.is_stolen, partner?.uuid, serialnumber])

  const onChange = (attribute) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTheft: THEFT_TYPE = { ...theft }
    newTheft[attribute] =
      e.target.value.toString() == "on" ? e.target.checked : e.target.value.toString()
    setTheft(newTheft)
  }

  const closeModal = () => setModal(false)
  const showModal = () => setModal(true)
  const onReportTheft = () => {
    if (!partner?.uuid) return
    setLoading(true)
    postPartnerDeviceSerialnumberThefts(partner.uuid, serialnumber, {
      circumstanceofcrime: theft.description,
      specialfeatures: theft.specialFeature,
      stolen_timestamp_from: toApiTimestamp(theft.startDate, theft.startTime),
      stolen_timestamp_to: toApiTimestamp(theft.endDate, theft.endTime),
      police_case_number: theft.police_case_number || undefined,
    }).then(({ response }) => {
      if (response.ok) {
        fetchDevice().then(() => {
          closeModal()
          setLoading(false)
        })
      } else {
        setLoading(false)
      }
    })
  }
  const onPersistPoliceNumber = (text: string) => {
    if (activeTheft == null || savingPoliceNumber) return
    setSavingPoliceNumber(true)
    patchTheftsId(activeTheft.id, { police_case_number: text || null }).then(
      ({ response, data }) => {
        setSavingPoliceNumber(false)
        if (response.ok && data) {
          setActiveTheft(data)
        }
      },
    )
  }
  const onDeviceFound = () => {
    if (activeTheft == null) return
    setLoading(true)
    deleteTheftsId(activeTheft.id).then(({ response }) => {
      if (response.ok) {
        setActiveTheft(null)
        fetchDevice().then(() => setLoading(false))
      } else {
        setLoading(false)
      }
    })
  }

  return (
    <Page showHeader headerValue={t("sidebar.theft_report")}>
      <Form.Label>{t("theft.stolen_device")}</Form.Label>
      <div style={styles.headerView}>
        <Form.Control
          type="search"
          id="search"
          placeholder={t("theft.stolen_device_input")}
          value={serialnumber || serialnumberInput}
          disabled={!!serialnumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSerialnumberInput(e.target.value.toString())
          }}
        />
        {serialnumber && (
          <Button
            variant="light"
            style={styles.buttonView}
            onClick={() => {
              setDevice(null)
              setSerialnumber(null)
            }}
          >
            <Icon.X className="bi" />
          </Button>
        )}
      </div>
      {!serialnumber && serialnumberInput.length > 0 && (
        <DeviceSearch
          searchText={serialnumberInput}
          onClick={(device) => {
            setSerialnumber(device.serialnumber)
          }}
        />
      )}
      {serialnumber && (
        <>
          <div className="mb-3">
            {device?.icon && <Image rounded src={device?.icon} width={70} className="me-2" />}
            <Form.Label>{device?.name}</Form.Label>
          </div>
          {!device?.is_stolen ? (
            <>
              <Form.Label>{t("theft.time_period")}</Form.Label>
              <Row className="mb-3">
                <Col>
                  <Form.Label>{t("theft.start")}:</Form.Label>
                  <div style={styles.displayFlex}>
                    <Form.Control
                      type="date"
                      value={theft.startDate}
                      onChange={onChange("startDate")}
                      className="me-1"
                      max={theft.endDate || today}
                    />
                    <Form.Control
                      type="time"
                      value={theft.startTime}
                      onChange={onChange("startTime")}
                    />
                  </div>
                </Col>
                <Col>
                  <Form.Label>{t("theft.end")}:</Form.Label>
                  <div style={styles.displayFlex}>
                    <Form.Control
                      type="date"
                      value={theft.endDate}
                      onChange={onChange("endDate")}
                      className="me-1"
                      min={theft.startDate}
                      max={today}
                    />
                    <Form.Control
                      type="time"
                      value={theft.endTime}
                      onChange={onChange("endTime")}
                    />
                  </div>
                </Col>
              </Row>
              {!isStartBeforeEnd && theftStartAndEndFilled && (
                <p className="text-danger small">{t("theft.range_error")}</p>
              )}
              <Form.Label>{t("theft.special_features")}</Form.Label>
              <Form.Control
                as="textarea"
                className="mb-3"
                value={theft.specialFeature}
                onChange={onChange("specialFeature")}
              />
              <Form.Label>{t("theft.description")}</Form.Label>
              <Form.Control
                as="textarea"
                className="mb-3"
                value={theft.description}
                onChange={onChange("description")}
              />
              <Form.Label>{t("theft.police_case_number")}</Form.Label>
              <Form.Control
                type="text"
                className="mb-3"
                placeholder={t("theft.police_case_number")}
                value={theft.police_case_number}
                onChange={onChange("police_case_number")}
              />
              <Form.Text muted>{t("theft.explanation")}</Form.Text>
              <InputGroup className="mt-2 mb-3">
                <Form.Check
                  label={t("theft.chackbox_label")}
                  checked={theft.checkbox}
                  onChange={onChange("checkbox")}
                />
              </InputGroup>
              <Button onClick={showModal} disabled={!reportTheftAllowed}>
                {t("theft.report_theft")}
              </Button>
              <Modal show={modal} onHide={closeModal}>
                <Modal.Header closeButton>
                  <Modal.Title>{t("theft.report_theft")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{t("theft.warning")}</Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={closeModal}>
                    {t("theft.close")}
                  </Button>
                  <Button variant="primary" onClick={onReportTheft}>
                    {loading ? (
                      <Spinner animation="border" size="sm" variant="light" />
                    ) : (
                      t("theft.report_theft_now")
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>
            </>
          ) : (
            <>
              <Form.Label>{t("theft.report_successful")}</Form.Label>
              <Form.Label>{t("theft.report_police")}</Form.Label>
              {activeTheft == null ? (
                activeTheftLoadFailed ? (
                  <p className="text-danger small">{t("theft.unable_to_load_theft_details")}</p>
                ) : (
                  <p className="text-muted small">{t("theft.loading_theft_details")}</p>
                )
              ) : (
                <>
                  <InputField
                    name="theft-police-case-number"
                    label={t("theft.police_case_number")}
                    value={activeTheft.police_case_number ?? ""}
                    onPersist={onPersistPoliceNumber}
                    loading={savingPoliceNumber}
                    placeholder={t("theft.police_case_number_placeholder")}
                  />
                  <Button variant="success" onClick={onDeviceFound} className="me-2 mt-2">
                    {loading ? (
                      <Spinner animation="border" size="sm" variant="light" />
                    ) : (
                      t("theft.device_found")
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </>
      )}
    </Page>
  )
}
